import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useGroupDetail, useLeaveGroup, useRegenerateInviteCode } from '@/hooks/useGroups';
import { useGroupExpenses } from '@/hooks/useExpenses';
import { useGroupCommentCounts } from '@/hooks/useComments';
import { useGroupBalances } from '@/hooks/useBalances';
import { useGroupSettlements } from '@/hooks/useSettlements';
import { useStatistics, useStatsPeriodLabels } from '@/hooks/useStatistics';
import { useAuth } from '@/contexts/AuthContext';
import { usePremium } from '@/hooks/usePremium';
import { isGroupPremium as checkGroupPremium, isPremiumTier, hasFeatureInGroup } from '@/lib/premium';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { CategoryIcon } from '@/components/common/CategoryIcon';
import { ExpenseShareLabel } from '@/components/expenses/ExpenseShareLabel';
import { UserAvatar } from '@/components/common/UserAvatar';
import { fromMinorUnits, getMinorUnitsSync } from '@sharecost/shared';
import { useGroupRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { useGroupMemberLookup } from '@/hooks/useGroupMemberLookup';
import { EditGroupDialog } from '@/components/groups/EditGroupDialog';
import { AddVirtualMemberDialog } from '@/components/groups/AddVirtualMemberDialog';
import { BudgetProgressBar } from '@/components/groups/BudgetProgressBar';
import { BudgetLockedNotice } from '@/components/groups/BudgetLockedNotice';
import { useGroupBudget } from '@/hooks/useGroupBudget';
import { StatisticsFilters } from '@/components/statistics/StatisticsFilters';
import { KpiCards } from '@/components/statistics/KpiCards';
import { SpendingOverTimeChart } from '@/components/statistics/SpendingOverTimeChart';
import { CategoryBreakdownChart } from '@/components/statistics/CategoryBreakdownChart';
import { MemberContributionsChart } from '@/components/statistics/MemberContributionsChart';
import { BalanceOverTimeChart } from '@/components/statistics/BalanceOverTimeChart';
import { TopExpensesTable } from '@/components/statistics/TopExpensesTable';
import { supabase } from '@/lib/supabase';
import {
  Loader2,
  Plus,
  Receipt,
  Users,
  TrendingUp,
  History,
  Copy,
  Check,
  ArrowRight,
  Handshake,
  Settings,
  UserPlus,
  Trash2,
  MessageCircle,
  Image,
  MapPin,
  TriangleAlert,
  BarChart3,
  LogOut,
  RefreshCw,
  AlertTriangle,
  Crown,
  Lock,
  Upload,
} from 'lucide-react';
import type { TimeRange } from '@/api/statistics';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

type Tab = 'expenses' | 'balances' | 'members' | 'settlements' | 'statistics';

export function GroupDetailPage() {
  const { t } = useTranslation();
  const { id: groupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPremium, tier } = usePremium();
  const [activeTab, setActiveTab] = useState<Tab>('expenses');
  const [copiedCode, setCopiedCode] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [virtualMemberDialogOpen, setVirtualMemberDialogOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('3M');
  const [periodOffset, setPeriodOffset] = useState(0);
  const queryClient = useQueryClient();

  const { data: group, isLoading: groupLoading } = useGroupDetail(groupId);

  // Group deleted, or membership ended, on another device while open here:
  // the realtime-triggered refetch resolves to null. Leave to the group list
  // rather than rendering a dead page. A direct open of an unknown group was
  // never shown and keeps the "not found" state.
  const wasShownRef = useRef(false);
  useEffect(() => {
    if (group) {
      wasShownRef.current = true;
    }
  }, [group]);
  useEffect(() => {
    if (!groupLoading && group === null && wasShownRef.current) {
      navigate('/groups', { replace: true });
    }
  }, [groupLoading, group, navigate]);

  const { data: expenses, isLoading: expensesLoading } = useGroupExpenses(groupId);
  const { data: balanceData } = useGroupBalances(groupId, group?.currency_code);
  const { data: settlements } = useGroupSettlements(groupId);
  const { data: commentCounts } = useGroupCommentCounts(groupId);
  const { data: stats, isLoading: statsLoading } = useStatistics(timeRange, groupId, periodOffset);
  const periodLabels = useStatsPeriodLabels(timeRange, periodOffset);
  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    setPeriodOffset(0);
  };
  const { data: memberLookup } = useGroupMemberLookup(groupId);
  const { data: budgetStatus } = useGroupBudget(group);

  const leaveGroupMut = useLeaveGroup();
  const regenerateInvite = useRegenerateInviteCode();

  // Live updates via Supabase Realtime
  useGroupRealtimeSubscription(groupId);

  if (groupLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-12">
        <p className="text-text-tertiary">{t('groups:settings.alert.groupNotFound')}</p>
      </div>
    );
  }

  const handleCopyInviteCode = async () => {
    if (group.invite_code) {
      await navigator.clipboard.writeText(group.invite_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleDeleteVirtualMember = async (memberId: string, userId: string, displayName: string) => {
    if (!window.confirm(`Remove virtual member "${displayName}" from this group?`)) return;

    try {
      // Soft-delete group membership
      await supabase
        .from('group_members')
        .update({ _deleted: true })
        .eq('id', memberId);

      // Soft-delete virtual user
      await supabase
        .from('users')
        .update({ _deleted: true })
        .eq('id', userId);

      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    } catch (err) {
      console.error('Failed to delete virtual member:', err);
    }
  };

  const myBalance = balanceData?.balances.find(b => b.userId === user?.id);
  const isAdmin = group.members.some(m => m.user_id === user?.id && m.role === 'admin');
  const hasBalance = myBalance ? myBalance.balanceMinor !== 0 : false;

  const inviteExpired = group.invite_code_expires_at
    ? new Date(group.invite_code_expires_at) < new Date()
    : false;

  const groupPremiumActive = checkGroupPremium(group.members);


  const handleRegenerateInviteCode = async () => {
    if (!window.confirm(t('web:groupDetailPage.regenerateInviteCodeTheOldCode'))) return;
    regenerateInvite.mutate({ groupId: group.id });
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm(t('web:common.areYouSureYouWantTo'))) return;
    try {
      await leaveGroupMut.mutateAsync(group.id);
      navigate('/dashboard');
    } catch {
      // Error shown inline
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'expenses', label: t('common:tab.expenses'), icon: Receipt },
    { id: 'balances', label: t('groups:detail.tab.balances'), icon: TrendingUp },
    { id: 'members', label: t('expenses:filter.members'), icon: Users },
    { id: 'settlements', label: t('home:allActivity.filter.settlements'), icon: History },
    { id: 'statistics', label: t('statistics:title'), icon: BarChart3 },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Group header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div
            className="mt-1 h-5 w-5 rounded-full shrink-0"
            style={{ backgroundColor: group.color ?? '#6366F1' }}
          />
          <div>
            <h1 className="text-2xl font-bold text-text-primary">{group.name}</h1>
            {group.description && <p className="mt-0.5 text-text-tertiary">{group.description}</p>}
            <div className="mt-2 flex items-center gap-3 text-sm text-text-tertiary">
              <span>{group.currency_code}</span>
              <span>{t('web:groupDetailPage.membersCount', { count: group.members.length })}</span>
              {myBalance && myBalance.balanceMinor !== 0 && (
                <span>
                  {t('groups:screen.yourBalance')}{' '}
                  <AmountDisplay
                    amountMinor={myBalance.balanceMinor}
                    currency={group.currency_code}
                    colorize
                    className="font-medium"
                  />
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {group.invite_code && (
            <div className="flex items-center gap-1">
              <button
                onClick={inviteExpired ? undefined : handleCopyInviteCode}
                disabled={inviteExpired}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm ${
                  inviteExpired
                    ? 'border-red-300 dark:border-red-800 text-red-500 dark:text-red-400 line-through opacity-60 cursor-not-allowed'
                    : 'border-border-secondary text-text-secondary hover:bg-surface-hover'
                }`}
                title={inviteExpired ? t('web:groupDetailPage.inviteCodeExpired') : t('web:groupDetailPage.copyInviteCode')}
              >
                {inviteExpired ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : copiedCode ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copiedCode && !inviteExpired ? t('common:button.copied') : group.invite_code}
              </button>
              {inviteExpired && (
                <span className="rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                  {t('web:groupDetailPage.expired')}
                </span>
              )}
              {isAdmin && (
                <button
                  onClick={handleRegenerateInviteCode}
                  disabled={regenerateInvite.isPending}
                  className="rounded-lg border border-border-secondary p-1.5 text-text-secondary hover:bg-surface-hover disabled:opacity-50"
                  title={t('web:groupDetailPage.regenerateInviteCode')}
                >
                  <RefreshCw className={`h-4 w-4 ${regenerateInvite.isPending ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>
          )}
          {!group.invite_code && isAdmin && (
            <button
              onClick={() => regenerateInvite.mutate({ groupId: group.id })}
              disabled={regenerateInvite.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${regenerateInvite.isPending ? 'animate-spin' : ''}`} />
              {t('onboarding:tutorial.invite-friends.slide1.title')}
            </button>
          )}
          {!isAdmin && (
            <button
              onClick={handleLeaveGroup}
              disabled={hasBalance || leaveGroupMut.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-800 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
              title={hasBalance ? t('web:groupDetailPage.settleYourBalanceBeforeLeaving') : t('web:groupDetailPage.leaveThisGroup')}
            >
              {leaveGroupMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              {t('groups:settings.alert.leaveButton')}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setEditDialogOpen(true)}
              className="rounded-lg border border-border-secondary p-1.5 text-text-secondary hover:bg-surface-hover"
              title={t('groups:settings.title')}
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => navigate(`/groups/${groupId}/import`)}
            className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover"
            title={t('web:groupDetailPage.importExpensesFromAnotherApp')}
          >
            <Upload className="h-4 w-4" />
            {t('web:common.import')}
          </button>
          <button
            onClick={() => navigate(`/groups/${groupId}/add`)}
            className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium bg-brand-600 text-white hover:bg-brand-700"
          >
            <Plus className="h-4 w-4" />
            {t('web:groupDetailPage.expense')}
          </button>
          <button
            onClick={() => navigate(`/groups/${groupId}/settle`)}
            className="flex items-center gap-1 rounded-lg border border-brand-600 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30"
          >
            <Handshake className="h-4 w-4" />
            {t('groups:detail.action.settleUp')}
          </button>
        </div>
      </div>

      {/* Budget progress (when enabled) */}
      {group.budget_enabled && (group.budget_amount_minor ?? 0) > 0 && (
        <div className="rounded-xl bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary">
          {budgetStatus && hasFeatureInGroup(tier, 'group_budgets', group.members) ? (
            <BudgetProgressBar status={budgetStatus} />
          ) : (
            <BudgetLockedNotice group={group} />
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-border-primary">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'expenses' && (
        <div className="space-y-2">
          {expensesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : expenses && expenses.length > 0 ? (
            expenses.map(expense => (
              <button
                key={expense.id}
                onClick={() => navigate(`/expenses/${expense.id}`)}
                className="flex w-full items-center gap-4 rounded-lg bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary text-left hover:shadow-md transition-shadow"
              >
                {/* Category icon */}
                <CategoryIcon
                  icon={expense.category?.icon}
                  color={expense.category?.color}
                />

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary truncate">{expense.description}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <p className="text-xs text-text-tertiary">
                      {t('expenses:detail.paidBy')} {expense.paid_by_user?.display_name ?? memberLookup?.get(expense.paid_by)?.displayName ?? t('web:common.unknownUser')} &middot;{' '}
                      {format(new Date(expense.expense_date), 'MMM d, yyyy')}
                    </p>
                    {(expense.has_open_dispute || commentCounts?.[expense.id] || expense.receipt_url || expense.location_name || expense.latitude) && (
                      <div className="flex items-center gap-1">
                        {expense.has_open_dispute && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-red-500/15 px-1.5 py-0.5 text-red-600 dark:text-red-400" title={t('web:common.hasOpenDispute')}>
                            <TriangleAlert className="h-3 w-3" />
                            <span className="text-[10px] font-medium">{t('expenses:swipeable.dispute')}</span>
                          </span>
                        )}
                        {commentCounts?.[expense.id] && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-blue-600 dark:text-blue-400" title={t('web:common.comments', { count: commentCounts[expense.id] })}>
                            <MessageCircle className="h-3 w-3" />
                            <span className="text-[10px] font-medium">{commentCounts[expense.id]}</span>
                          </span>
                        )}
                        {expense.receipt_url && (
                          <span className="inline-flex items-center rounded-full bg-emerald-500/15 p-1 text-emerald-600 dark:text-emerald-400" title={t('web:common.hasReceipt')}>
                            <Image className="h-3 w-3" />
                          </span>
                        )}
                        {(expense.location_name || expense.latitude) && (
                          <span className="inline-flex items-center rounded-full bg-amber-500/15 p-1 text-amber-600 dark:text-amber-400" title={expense.location_name ?? t('web:common.hasLocation')}>
                            <MapPin className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount + user's share */}
                <div className="text-right shrink-0">
                  <AmountDisplay
                    amountMinor={Number(expense.amount_minor_src)}
                    currency={expense.currency_code_src}
                    className="font-semibold text-text-primary"
                  />
                  <ExpenseShareLabel expense={expense} userId={user?.id} />
                </div>
              </button>
            ))
          ) : (
            <div className="text-center py-8 text-text-tertiary">
              <Receipt className="mx-auto h-8 w-8 text-text-muted mb-2" />
              {t('web:groupDetailPage.noExpensesYetAddYourFirst')}
            </div>
          )}
        </div>
      )}

      {activeTab === 'balances' && (
        <div className="space-y-4">
          {/* Per-user balances */}
          <div className="rounded-xl bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary">
            <h3 className="font-medium text-text-primary mb-3">{t('groups:stats.balances.title')}</h3>
            <div className="space-y-2">
              {balanceData?.balances
                .sort((a, b) => b.balanceMinor - a.balanceMinor)
                .map(balance => {
                  const member = group.members.find(m => m.user_id === balance.userId);
                  const lookup = memberLookup?.get(balance.userId);
                  const displayName = member?.user?.display_name ?? lookup?.displayName ?? t('web:common.unknownUser');
                  const avatarUrl = member?.user?.avatar_url ?? lookup?.avatarUrl;
                  return (
                    <div key={balance.userId} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <UserAvatar name={displayName} avatarUrl={avatarUrl} size="md" />
                        <span className="text-sm text-text-secondary">
                          {displayName}
                          {lookup?.isFormer && !member && (
                            <span className="ml-1 text-xs text-text-quaternary">(left)</span>
                          )}
                        </span>
                      </div>
                      <AmountDisplay
                        amountMinor={balance.balanceMinor}
                        currency={group.currency_code}
                        colorize
                        className="text-sm font-medium"
                      />
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Settlement suggestions */}
          {balanceData?.suggestions && balanceData.suggestions.length > 0 && (
            <div className="rounded-xl bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary">
              <h3 className="font-medium text-text-primary mb-3">{t('groups:balances.suggested')}</h3>
              <div className="space-y-3">
                {balanceData.suggestions.map((s, i) => {
                  const from = group.members.find(m => m.user_id === s.fromUserId);
                  const to = group.members.find(m => m.user_id === s.toUserId);
                  const fromName = from?.user?.display_name ?? memberLookup?.get(s.fromUserId)?.displayName ?? t('web:common.unknownUser');
                  const toName = to?.user?.display_name ?? memberLookup?.get(s.toUserId)?.displayName ?? t('web:common.unknownUser');
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-lg bg-surface-secondary p-3">
                      <span className="text-sm font-medium text-text-secondary">
                        {fromName}
                      </span>
                      <ArrowRight className="h-4 w-4 text-text-quaternary shrink-0" />
                      <span className="text-sm font-medium text-text-secondary">
                        {toName}
                      </span>
                      <AmountDisplay
                        amountMinor={s.amountMinor}
                        currency={group.currency_code}
                        className="ml-auto text-sm font-semibold text-brand-600"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="space-y-3">
          <button
            onClick={() => setVirtualMemberDialogOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-dashed border-border-secondary px-4 py-2.5 text-sm font-medium text-text-secondary hover:border-brand-400 hover:text-brand-600 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            {t('groups:addMemberModal.addVirtualMember')}
          </button>

          <div className="rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary divide-y divide-border-tertiary">
            {group.members.map(member => {
              const isVirtual = member.user?.is_virtual === true;
              const canDelete = isVirtual && member.user?.created_by_user_id === user?.id;

              return (
                <div key={member.id} className="flex items-center gap-3 p-4">
                  <UserAvatar name={member.user?.display_name} avatarUrl={member.user?.avatar_url} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {member.user?.display_name}
                        {member.user_id === user?.id && (
                          <span className="ml-1 text-xs text-text-quaternary">{t('web:common.youSuffix')}</span>
                        )}
                      </p>
                      {isVirtual && (
                        <span className="shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                          {t('groups:role.virtual')}
                        </span>
                      )}
                      {!isVirtual && isPremiumTier(member.user?.subscription_tier) && (
                        <span className="flex items-center gap-1 shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                          <Crown className="h-3 w-3" />
                          {t('groups:addMemberModal.premiumLabel')}
                        </span>
                      )}
                    </div>
                    {!isVirtual && member.user?.email && (
                      <p className="text-xs text-text-tertiary truncate">{member.user.email}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        member.role === 'admin'
                          ? 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300'
                          : 'bg-surface-tertiary text-text-secondary'
                      }`}
                    >
                      {member.role}
                    </span>
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteVirtualMember(member.id, member.user_id, member.user?.display_name ?? t('web:common.unknownUser'))}
                        className="rounded-md p-1 text-text-quaternary hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        title={t('web:groupDetailPage.removeVirtualMember')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'settlements' && (
        <div className="space-y-2">
          {settlements && settlements.length > 0 ? (
            settlements.map(settlement => {
              const from = group.members.find(m => m.user_id === settlement.from_user_id);
              const to = group.members.find(m => m.user_id === settlement.to_user_id);
              const fromName = from?.user?.display_name ?? memberLookup?.get(settlement.from_user_id)?.displayName ?? settlement.from_user?.display_name ?? t('web:common.unknownUser');
              const toName = to?.user?.display_name ?? memberLookup?.get(settlement.to_user_id)?.displayName ?? settlement.to_user?.display_name ?? t('web:common.unknownUser');
              return (
                <div key={settlement.id} className="flex items-center gap-4 rounded-lg bg-surface-primary p-4 shadow-sm ring-1 ring-border-primary">
                  <Handshake className="h-5 w-5 text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary">
                      <span className="font-medium">{fromName}</span>
                      {' paid '}
                      <span className="font-medium">{toName}</span>
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {format(new Date(settlement.settlement_date), 'MMM d, yyyy')}
                      {settlement.description && ` — ${settlement.description}`}
                    </p>
                  </div>
                  <AmountDisplay
                    amountMinor={Number(settlement.amount_minor)}
                    currency={group.currency_code}
                    className="font-semibold text-green-600 dark:text-green-400 shrink-0"
                  />
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-text-tertiary">
              <Handshake className="mx-auto h-8 w-8 text-text-muted mb-2" />
              {t('web:groupDetailPage.noSettlementsYet')}
            </div>
          )}
        </div>
      )}

      {activeTab === 'statistics' && (
        <div className="space-y-6">
          <div className="space-y-6">
            <StatisticsFilters
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
              periodLabels={periodLabels}
              periodOffset={periodOffset}
              onPeriodOffsetChange={setPeriodOffset}
              onGroupChange={() => {}}
            />

            {statsLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
              </div>
            )}

            {stats && (
              <>
                {stats.kpi.expenseCount === 0 ? (
                  <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
                      <BarChart3 className="h-6 w-6 text-text-quaternary" />
                    </div>
                    <h3 className="mt-4 text-sm font-medium text-text-primary">{t('web:common.notEnoughData')}</h3>
                    <p className="mt-1 text-sm text-text-tertiary">
                      {t('web:common.noExpensesFoundForTheSelected')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <KpiCards data={stats.kpi} currency={stats.displayCurrency} />

                    <div className="grid gap-6 lg:grid-cols-2">
                      <SpendingOverTimeChart
                        data={stats.spendingOverTime}
                        currency={stats.displayCurrency}
                      />
                      <CategoryBreakdownChart
                        data={stats.categoryBreakdown}
                        currency={stats.displayCurrency}
                        totalSpent={stats.kpi.totalSpent}
                      />
                      <MemberContributionsChart
                        data={stats.memberContributions}
                        currency={stats.displayCurrency}
                      />
                      <BalanceOverTimeChart
                        data={stats.balanceOverTime}
                        currency={stats.displayCurrency}
                      />
                    </div>

                    <TopExpensesTable
                      data={stats.topExpenses}
                      currency={stats.displayCurrency}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <EditGroupDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        group={group}
        userId={user!.id}
        balances={balanceData?.balances}
        onGroupRemoved={() => navigate('/dashboard')}
      />
      <AddVirtualMemberDialog
        open={virtualMemberDialogOpen}
        onClose={() => setVirtualMemberDialogOpen(false)}
        groupId={group.id}
      />
    </div>
  );
}
